/**
 * CP4Reveal — portrait reveal screen.
 * data-screen-label="cp-04 Reveal · Living Sigil rose"
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CPBackdrop, CPStars, CPAvatar } from './shared';
import { LivingSigil } from '../../brand/LivingSigil';
import { AiGeneratedLabel } from '../../AiGeneratedLabel';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import styles from './Couple.module.css';

const R2_BASE_URL = (import.meta.env['VITE_R2_PUBLIC_BASE_URL'] as string | undefined) ?? 'https://cdn.calestea.com';

interface CP4RevealProps {
  flow: UseCoupleFlowReturn;
  onActions: () => void;
}

export function CP4Reveal({ flow, onActions }: CP4RevealProps): JSX.Element {
  const { t } = useTranslation('flows');
  const [imgLoaded, setImgLoaded] = useState(false);

  const portraitUrl = flow.portraitR2Key ? `${R2_BASE_URL}/${flow.portraitR2Key}` : null;

  return (
    <div className={styles.root} data-screen-label="cp-04 Reveal · Living Sigil rose">
      <CPBackdrop tone="rose" />
      <CPStars count={50} seed={42} />

      <div className={styles.content}>
        {/* Nav */}
        <div className={styles.navRow}>
          <button type="button" className={styles.navBack} onClick={onActions}>
            ← {t('couple.reveal.back')}
          </button>
          <span className={styles.navDots}>· · ·</span>
        </div>

        {/* Title */}
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <div className={styles.eyebrow}>{t('couple.reveal.eyebrow')}</div>
        </div>

        {/* Portrait card */}
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}
        >
          <div className={styles.portraitCard}>
            {!imgLoaded && <div className={styles.portraitSkeleton} />}
            {portraitUrl && (
              <img
                src={portraitUrl}
                alt={t('couple.reveal.portraitAlt')}
                className={styles.portraitImg}
                style={{ display: imgLoaded ? 'block' : 'none' }}
                onLoad={() => setImgLoaded(true)}
              />
            )}
          </div>
        </div>

        <AiGeneratedLabel />

        {/* Couple name */}
        {flow.coupleName && (
          <h2 className={styles.coupleNameHeading}>{flow.coupleName}</h2>
        )}

        {/* Form ≠ gender disclaimer (parity with the bot caption; self-containment) */}
        <p className={styles.formNote}>{t('couple.reveal.formNote')}</p>

        {/* Overlapping avatar pill */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div className={styles.avatarPill}>
            <CPAvatar glyph="🌙" palette="gold" size={40} />
            <div className={styles.avatarPillSecond}>
              <CPAvatar glyph="✦" palette="rose" size={40} />
            </div>
          </div>
        </div>

        {/* Decorative sigil */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <LivingSigil archetype="celestial-romantic" state="breathing" size={180} />
        </div>

        {/* CTA */}
        <div className={styles.ctaArea}>
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={onActions}
          >
            {t('couple.reveal.shareCta')}
          </button>
        </div>
      </div>
    </div>
  );
}
